import React, { useEffect, useState } from "react";
import { Form, Select } from "semantic-ui-react";
import RuvixxForm from "../../../components/RuvixxForm";
import EmailExemptionService from "../../../services/EmailExemptions";
import CampaignService from "../../../services/Campaign";
import EmailAddressService from "../../../services/EmailAddress";
import RuvixxSelect from "../../../components/RuvixxSelect";

/**
 * Form component for creating and editing email exemptions
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Callback function after successful submission
 * @param {boolean} props.isClone - Whether this is a clone operation
 * @param {number} props.emailExemptionId - ID of the exemption to edit/clone
 */
function EmailExemptionForm({ onSuccess, isClone, emailExemptionId }) {
  const [emailAddressId, setEmailAddressId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [emailOptions, setEmailOptions] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [justification, setJustification] = useState("");
  const [source, setSource] = useState("");
  const [notes, setNotes] = useState("");

  const fetchExemption = async exemption_id => {
    let exemption = await EmailExemptionService.getEmailExemption(exemption_id);
    setEmailAddressId(exemption.email_address.id);
    setCampaignId(exemption.campaign.id);
    setJustification(exemption.justification);
    setSource(exemption.source);
    setNotes(exemption.notes);
  };

  const fetchEmailAddresses = async () => {
    const emails = await EmailAddressService.getEmailAddressesForFilters();
    const options = emails.map(email => ({
      key: email.id,
      text: email.email,
      value: email.id,
    }));
    setEmailOptions(options);
  };

  const fetchCampaigns = async () => {
    const campaigns = await CampaignService.getCampaignsForFilters();
    const options = campaigns.map(campaign => ({
      key: campaign.id,
      text: campaign.name,
      value: campaign.id,
    }));
    setCampaignOptions(options);
  };

  const createExemptionData = () => ({
    email_address_id: emailAddressId,
    campaign_id: campaignId,
    justification: justification,
    source: source,
    notes: notes,
  });

  const handleSubmit = async () => {
    const exemptionData = createExemptionData();
    if (emailExemptionId) {
      if (isClone) {
        await EmailExemptionService.createEmailExemption(exemptionData);
      } else {
        await EmailExemptionService.editEmailExemption(
          emailExemptionId,
          exemptionData
        );
      }
    } else {
      await EmailExemptionService.createEmailExemption(exemptionData);
    }
  };

  const handleEmailChange = (e, data) => {
    setEmailAddressId(data.value);
  };

  const handleCampaignChange = (e, data) => {
    setCampaignId(data.value);
  };

  const handleJustificationChange = (e, data) => {
    setJustification(data.value);
  };

  const handleSourceChange = (e, data) => {
    setSource(data.value);
  };

  const handleNotesChange = (e, data) => {
    setNotes(data.value);
  };

  useEffect(() => {
    if (emailExemptionId) {
      fetchExemption(emailExemptionId);
    }
    fetchEmailAddresses();
    fetchCampaigns();
  }, [emailExemptionId]);

  return (
    <RuvixxForm
      ready={!!emailAddressId && !!campaignId && !!justification && !!source}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
      successHeader="Email Exemption Created"
      successMessage="Successfully created a new email exemption"
    >
      <Form.Field inline>
        <label>Email Address</label>
        <Select
          search
          required
          name="emailAddressId"
          value={emailAddressId}
          onChange={handleEmailChange}
          placeholder="Select an email address"
          options={emailOptions}
        />
      </Form.Field>
      <RuvixxSelect
        required
        label="Campaign"
        name="campaignId"
        queryFn={CampaignService.getCampaignsForFilters}
        value={campaignId}
        onChange={handleCampaignChange}
        placeholder="Select a campaign"
      />
      <Form.TextArea
        required
        label="Justification"
        name="justification"
        placeholder="Enter the justification for the email exemption"
        value={justification}
        onChange={handleJustificationChange}
      />
      <Form.Input
        required
        label="Source"
        name="source"
        placeholder="Enter the source of the email exemption"
        value={source}
        onChange={handleSourceChange}
      />
      <Form.TextArea
        label="Notes"
        name="notes"
        placeholder="Enter any additional notes for the email exemption"
        value={notes}
        onChange={handleNotesChange}
      />
    </RuvixxForm>
  );
}

export default EmailExemptionForm;
